import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ProjectTable = ({ 
  projects, 
  selectedProjects, 
  onSelectProject, 
  onSelectAll, 
  onEditProject, 
  onDuplicateProject, 
  onExportProject, 
  onDeleteProject,
  sortConfig,
  onSort 
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    onSort({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ArrowUp" size={14} className="text-primary" />
      : <Icon name="ArrowDown" size={14} className="text-primary" />;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-yellow-100 text-yellow-800', label: 'Draft' },
      completed: { color: 'bg-green-100 text-green-800', label: 'Completed' },
      rendering: { color: 'bg-blue-100 text-blue-800', label: 'Rendering' },
      error: { color: 'bg-red-100 text-red-800', label: 'Error' }
    };

    const config = statusConfig?.[status] || statusConfig?.draft;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleDeleteClick = (project) => {
    setShowDeleteConfirm(project?.id);
  };

  const confirmDelete = (projectId) => {
    onDeleteProject(projectId);
    setShowDeleteConfirm(null);
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <Checkbox
                  checked={selectedProjects?.length === projects?.length && projects?.length > 0}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                  indeterminate={selectedProjects?.length > 0 && selectedProjects?.length < projects?.length}
                />
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-micro"
                >
                  <span>Project Name</span>
                  {getSortIcon('name')}
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => handleSort('createdAt')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-micro"
                >
                  <span>Created</span>
                  {getSortIcon('createdAt')}
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => handleSort('modifiedAt')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-micro"
                >
                  <span>Modified</span>
                  {getSortIcon('modifiedAt')}
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-micro"
                >
                  <span>Status</span>
                  {getSortIcon('status')}
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => handleSort('credits')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-micro"
                >
                  <span>Credits Created</span>
                  {getSortIcon('credits')}
                </button>
              </th>
              <th className="text-right px-4 py-3">
                <span className="text-sm font-medium text-foreground">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {projects?.map((project) => (
              <tr key={project?.id} className="hover:bg-muted/30 transition-micro">
                <td className="px-4 py-4">
                  <Checkbox
                    checked={selectedProjects?.includes(project?.id)}
                    onChange={(e) => onSelectProject(project?.id, e?.target?.checked)}
                  />
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Film" size={16} className="text-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{project?.name}</div>
                      <div className="text-sm text-muted-foreground">{project?.type}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-muted-foreground">
                  {formatDate(project?.createdAt)}
                </td>
                <td className="px-4 py-4 text-sm text-muted-foreground">
                  {formatDate(project?.modifiedAt)}
                </td>
                <td className="px-4 py-4">
                  {getStatusBadge(project?.status)}
                </td>
                <td className="px-4 py-4 text-sm text-muted-foreground">
                  {project?.credits || 0}
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-end space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit"
                      onClick={() => onEditProject(project?.id)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Copy"
                      onClick={() => onDuplicateProject(project?.id)}
                    >
                      Duplicate
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Download"
                      onClick={() => onExportProject(project?.id)}
                    >
                      Export
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => handleDeleteClick(project)}
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4 p-4">
        {projects?.map((project) => (
          <div key={project?.id} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <Checkbox
                  checked={selectedProjects?.includes(project?.id)}
                  onChange={(e) => onSelectProject(project?.id, e?.target?.checked)}
                />
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Film" size={20} className="text-primary" />
                </div>
                <div>
                  <div className="font-medium text-foreground">{project?.name}</div>
                  <div className="text-sm text-muted-foreground">{project?.type}</div>
                </div>
              </div>
              {getStatusBadge(project?.status)}
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
              <div>
                <span className="text-muted-foreground">Created:</span>
                <div className="font-medium">{formatDate(project?.createdAt)}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Modified:</span>
                <div className="font-medium">{formatDate(project?.modifiedAt)}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Size:</span>
                <div className="font-medium">{formatFileSize(project?.fileSize)}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Credits:</span>
                <div className="font-medium">{project?.credits || 0}</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Edit"
                onClick={() => onEditProject(project?.id)}
              >
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Copy"
                onClick={() => onDuplicateProject(project?.id)}
              >
                Duplicate
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                onClick={() => onExportProject(project?.id)}
              >
                Export
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Trash2"
                onClick={() => handleDeleteClick(project)}
              >
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full shadow-elevation-3">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Delete Project</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground mb-6">
              Are you sure you want to delete this project? All associated data will be permanently removed.
            </p>
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(null)}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => confirmDelete(showDeleteConfirm)}
                fullWidth
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectTable;